/**
 * Part 4 — Recovery Suggestions panel for a single risk assessment.
 * Shows the 8 recovery categories (recommended ones first) with their current
 * coverage status + enrichment reference, and a "Create draft plan" action that
 * generates a GPT-4o, enrichment-seeded recovery plan and recalculates residual.
 */
import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { CheckCircle2, Circle, AlertCircle, Sparkles, RefreshCw } from "lucide-react";

const STATUS_PILL = {
  tested: { label: "Tested", cls: "bg-emerald-100 text-emerald-700", Icon: CheckCircle2 },
  approved: { label: "Approved", cls: "bg-sky-100 text-sky-700", Icon: CheckCircle2 },
  draft: { label: "Draft", cls: "bg-amber-100 text-amber-700", Icon: AlertCircle },
  missing: { label: "Gap", cls: "bg-red-100 text-red-700", Icon: Circle },
};

export default function RecoverySuggestionsPanel({ assessmentId, onResidualChange }) {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/risk-advanced/assessment/${assessmentId}/recovery-suggestions`);
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [assessmentId]);

  const createPlan = async (category) => {
    setCreating(category);
    try {
      const res = await api.post(`/risk-advanced/assessment/${assessmentId}/create-recovery-plan`, { category });
      toast.success(`Draft plan created (${res.data.ai_source === "gpt-4o" ? "AI-generated" : "template"}).`);
      if (onResidualChange) onResidualChange(res.data.recalculated_residual);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create plan");
    } finally {
      setCreating(null);
    }
  };

  if (loading) {
    return <div className="text-xs text-slate-400 flex items-center gap-2 py-2" data-testid="recovery-suggestions-loading">
      <RefreshCw className="w-3.5 h-3.5 animate-spin" /> Loading recovery suggestions…</div>;
  }
  if (!data) return null;

  return (
    <div className="space-y-2" data-testid="recovery-suggestions-panel">
      {!data.show_suggestions && (
        <p className="text-[11px] text-slate-500">Inherent risk is {data.inherent_risk_level} — recovery categories are optional.</p>
      )}
      <div className="grid sm:grid-cols-2 gap-2">
        {data.suggestions.map((s) => {
          const pill = STATUS_PILL[s.current_status] || STATUS_PILL.missing;
          const Icon = pill.Icon;
          return (
            <div key={s.category}
              className={`rounded-lg border p-2.5 ${s.recommended ? "border-sky-200 bg-sky-50/40" : "border-slate-150 bg-white"}`}
              data-testid={`recovery-cat-${s.category}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5 min-w-0">
                  <Icon className={`w-3.5 h-3.5 shrink-0 ${s.current_status === "missing" ? "text-red-400" : "text-emerald-500"}`} />
                  <span className="text-xs font-semibold text-slate-700 truncate">{s.label}</span>
                  {s.recommended && <span className="text-[8px] font-bold px-1 py-0.5 rounded bg-sky-600 text-white uppercase">Rec</span>}
                </div>
                <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full shrink-0 ${pill.cls}`}>{pill.label}</span>
              </div>
              {s.enrichment_reference && (
                <p className="text-[10px] text-emerald-700 mt-1 truncate" title={s.enrichment_reference}>↳ {s.enrichment_reference}</p>
              )}
              <Button size="sm" variant="outline" disabled={creating === s.category}
                className="mt-2 h-6 text-[10px] px-2 w-full"
                onClick={() => createPlan(s.category)}
                data-testid={`create-plan-${s.category}`}>
                {creating === s.category
                  ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                  : <Sparkles className="w-3 h-3 mr-1" />}
                Create draft plan
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
